import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import InputNumber from 'rc-input-number';
import ModalWindow from '../../common/Modal';
import Button from '../../common/Button';
import Select from '../../common/Select';
import styles from './ReturnProductModal.module.scss';
import { numberFormatter, currencyFormat, numberOrZeroFormatter } from '../../../utils/formatHelper';
import RadioGroup from '../../common/RadioGroup';
import Radio from '../../common/Radio';
import { isEmpty, flatMap } from 'lodash';

const ReturnLineStatus = {
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
};

const AcceptType = {
  PARTIAL_ACCEPTED: 'partial_accepted',
  PARTIAL_DECLINED: 'partial_declined',
  ALL_ACCEPTED: 'all_accepted',
  ALL_DECLINED: 'all_declined',
};

function ReturnProductModal(props) {
  const { data, onClose } = props;
  const { fulfillments } = data;

  const [returnType, setReturnType] = useState(AcceptType.PARTIAL_ACCEPTED);
  const [values, setValues] = useState({});
  const [reasonCode, setReasonCode] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();

    let fulfillments = [];

    if (returnType === AcceptType.ALL_ACCEPTED || returnType === AcceptType.ALL_DECLINED) {
      fulfillments = data.fulfillments.map((item) => ({
        id: item.id,
        order_id: item.order_id,
        return_lines: item.fulfillment_lines.map((line) => ({
          quantity: line.quantity - (line.returned_items ? line.returned_items : 0),
          fulfillment_line_id: line.id,
          status: returnType === AcceptType.ALL_ACCEPTED ? ReturnLineStatus.ACCEPTED : ReturnLineStatus.DECLINED,
        })),
      }));
    } else {
      fulfillments = Object.keys(values).map((id) => {
        const item = values[id];
        return {
          id: +id,
          order_id: data.id,
          return_lines: Object.keys(item).map((key) => ({
            quantity: +item[key],
            fulfillment_line_id: +key,
            status: returnType === AcceptType.PARTIAL_ACCEPTED ? ReturnLineStatus.ACCEPTED : ReturnLineStatus.DECLINED,
          })),
        };
      });
    }

    fulfillments.forEach((fulfillment) => {
      const data = {
        return_lines: fulfillment.return_lines,
        reason_code: reasonCode,
        reason,
      };

      props.postReturns(data, fulfillment.order_id, fulfillment.id);
    });

    onClose();
  };

  const handleChangeQuantity = (fulfillmentId, id, value) => {
    setValues((prevValues) => {
      const values = {
        ...prevValues,
        [fulfillmentId]: {
          ...prevValues[fulfillmentId],
        },
      };

      if (value === 0) {
        delete values[fulfillmentId][id];

        if (isEmpty(values[fulfillmentId])) {
          delete values[fulfillmentId];
        }
      } else {
        values[fulfillmentId][id] = value;
      }

      return values;
    });
  };

  const returnNowFormatter = (cell, row, rowIndex, { values, handleChangeQuantity }) => {
    const itemValue = values[row.fulfillment_id] ? values[row.fulfillment_id][row.id] : 0;
    const max = row.quantity - row.returned_items;

    return (
      <InputNumber
        min={0}
        max={max}
        style={{ width: 80 }}
        value={itemValue ? itemValue : 0}
        onChange={(value) => handleChangeQuantity(row.fulfillment_id, row.id, value)}
        formatter={numberFormatter}
      />
    );
  };

  const buttonIsDisabled = () => {
    return (
      reasonCode === '' ||
      ((returnType === AcceptType.PARTIAL_ACCEPTED || returnType === AcceptType.PARTIAL_DECLINED) && isEmpty(values))
    );
  };

  const returnReasonsOptions = () => {
    const reasons = props.returnReasons.map((reason) => ({
      label: reason.value,
      value: reason.key,
    }));

    reasons.unshift({ label: 'Please select a return reason', value: '' });
    return reasons;
  };

  const fulfillmentsReturns = flatMap(fulfillments, (fulfillment) => fulfillment.fulfillment_returns);
  const fulfillmentsReturnLines = flatMap(
    fulfillmentsReturns,
    (fulfillmentReturn) => fulfillmentReturn.fulfillment_return_lines,
  );

  const shipmentRows = {};
  const returnedRows = {};
  const rows = flatMap(fulfillments, (fulfillment) => {
    fulfillment.fulfillment_lines.forEach((fulfillmentLine) => {
      shipmentRows[fulfillmentLine.variant_id] = 0;
      returnedRows[fulfillmentLine.id] = 0;
    });

    fulfillment.fulfillment_shipments.forEach((fulfillmentShipment) => {
      fulfillmentShipment.fulfillment_shipment_lines.forEach((fulfillmentShipmentItem) => {
        shipmentRows[fulfillmentShipmentItem.variant_id] += fulfillmentShipmentItem.quantity;
      });
    });

    fulfillmentsReturnLines.forEach((fulfillmentReturnLine) => {
      returnedRows[fulfillmentReturnLine.fulfillment_line_id] += fulfillmentReturnLine.quantity;
    });

    return fulfillment.fulfillment_lines.map((fulfillmentLine) => ({
      ...fulfillmentLine,
      quantity: shipmentRows[fulfillmentLine.variant_id],
      returned_items: returnedRows[fulfillmentLine.id],
    }));
  });

  const canReturn = data.items_total_quantity > data.returned_items_total_quantity;
  const hasReturned = data.returned_items_total_quantity > 0;
  const totalValue = hasReturned ? data.items_total_value - data.returned_items_total_value : data.items_total_value;
  const totalTax = hasReturned ? data.items_total_tax - data.returned_items_total_tax : data.items_total_tax;
  const totalQuantity = hasReturned
    ? data.items_total_quantity - data.returned_items_total_quantity
    : data.items_total_quantity;

  const columns = [
    {
      text: 'Title',
      dataField: 'variant.title',
    },
    {
      text: 'SKU',
      dataField: 'variant.sku',
    },
    {
      text: 'Warehouse',
      dataField: 'fulfillment_id',
      formatter: (cell) => <span>{fulfillments.find((x) => x.id === cell).warehouse.name}</span>,
    },
    {
      text: 'Shipped',
      dataField: 'quantity',
    },
    {
      text: 'Returned',
      dataField: 'returned_items',
      formatter: numberOrZeroFormatter,
    },
    {
      text: returnType === AcceptType.PARTIAL_ACCEPTED ? 'Return Now' : 'Decline now',
      dataField: 'id',
      formatter: returnNowFormatter,
      formatExtraData: {
        values,
        handleChangeQuantity,
      },
    },
  ];

  return (
    <ModalWindow open={props.open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <h2>Approve a return</h2>

        <hr />

        <div className={styles.body}>
          <div className={styles.details}>
            <h3 className={styles.title}>Order details:</h3>
            <div className={styles.info}>
              <dl>
                <dt>ID:</dt>
                <dd data-testid="title">{data.retailer_order_id}</dd>
                <dt>Date:</dt>
                <dd>{new Date(data.created_at).toLocaleDateString()}</dd>
              </dl>
              <dl>
                <dt>Retailer/Channel:</dt>
                <dd>{data.retailer.name}</dd>
                <dt>Type:</dt>
                <dd>{data.type}</dd>
              </dl>
              <dl>
                <dt>Value:</dt>
                <dd>{currencyFormat(totalValue, data.currency)}</dd>

                <dt>Tax:</dt>
                <dd>{currencyFormat(totalTax, data.currency)}</dd>

                <dt>Number of Units / Cases:</dt>
                <dd>{totalQuantity}</dd>
              </dl>
              <dl>
                <dt>Shipping:</dt>
                <dd>{currencyFormat(data.shipping_total_value, data.currency)}</dd>

                <dt>Shipping Tax:</dt>
                <dd>{currencyFormat(data.shipping_total_tax, data.currency)}</dd>
              </dl>
            </div>
          </div>

          <hr />
          <>
            <div className={styles.switcher}>
              <RadioGroup
                row
                name={`order_${data.id}`}
                selectedvalue={returnType}
                onChange={(value) => setReturnType(value)}
              >
                <Radio id={`all_${data.id}`} value={AcceptType.ALL_ACCEPTED} label="Full return" />
                <Radio id={`partial_accept${data.id}`} value={AcceptType.PARTIAL_ACCEPTED} label="Partial return" />
                <Radio id={`all_decline_${data.id}`} value={AcceptType.ALL_DECLINED} label="Full Decline" />
                <Radio id={`partial_decline${data.id}`} value={AcceptType.PARTIAL_DECLINED} label="Partial Decline" />
              </RadioGroup>
            </div>

            {fulfillments.length > 0 &&
              (returnType === AcceptType.PARTIAL_ACCEPTED || returnType === AcceptType.PARTIAL_DECLINED) && (
                <BootstrapTable
                  data={rows}
                  columns={columns}
                  keyField="id"
                  striped
                  bordered={false}
                  hover
                ></BootstrapTable>
              )}

            <div className={styles.form}>
              <label>
                <span>Return reason</span>
                <Select
                  options={returnReasonsOptions()}
                  name="reasonCode"
                  value={reasonCode}
                  onChange={(value) => setReasonCode(value)}
                />
              </label>

              <label>
                <span>RA# (optional)</span>
                <input type="text" name="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              </label>
            </div>

            <div className={styles.info}>
              <span className="ic-info" />
              <span>Note: this process can not be undone</span>
            </div>
          </>
        </div>

        <hr />

        <div className={styles.footer}>
          <Button color="white" text="CANCEL" onClick={onClose} />

          {canReturn && <Button type="submit" text="OK" disabled={buttonIsDisabled()} />}
        </div>
      </form>
    </ModalWindow>
  );
}

export default ReturnProductModal;

ReturnProductModal.defaultProps = {
  open: false,
};

ReturnProductModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  postReturns: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    fulfillments: PropTypes.array.isRequired,
  }).isRequired,
  returnReasons: PropTypes.array.isRequired,
};
